// src/components/layout/AppSelector.tsx
// Suite app selector — reads apps[] from bootContext ONLY.
// No hardcoded apps. No backend registry call.

import { useState, useRef, useEffect } from 'react';
import { requestCanonicalAppLaunch } from '../../auth/canonicalAppLaunch';
import { Grid, ExternalLink, Check } from 'lucide-react';
import { useAppSelector } from '../../store/store';
import { selectBootApps } from '../../store/slices/bootContextSlice';
import { APP_ID } from '../../config/centralAuth';

export default function AppSelector() {
  const apps = useAppSelector(selectBootApps);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Filter out 'hidden' apps; sort by order ASC (Central's canonical ordering)
  const filtered = apps
    .filter((app) => app.status !== 'hidden')
    .sort((a, b) => a.order - b.order);

  // Always ensure the current app appears in the list as "selected".
  // Central may or may not include it in apps[] — inject a fallback entry if missing.
  // IMPORTANT: Central uses 'appId' field, not 'id'
  const hasCurrent = filtered.some((app) => app.appId === APP_ID);
  const visibleApps = hasCurrent
    ? filtered
    : [
        { appId: APP_ID, name: 'LevelUp CRM', webUrl: '', status: 'active' as const, order: 0 },
        ...filtered,
      ];

  // Dev-only: log received apps so engineers can verify Central is returning correct data
  // NEVER logs tokens — only appId, displayName, status, webUrl
  if (import.meta.env.DEV && apps.length > 0) {
    console.debug(
      '[AppSelector] apps from Central:',
      apps.map((a) => ({ appId: a.appId, displayName: a.name, status: a.status, webUrl: a.webUrl }))
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Switch application"
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Grid className="w-5 h-5 text-gray-600" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
            Apps
          </p>
          {visibleApps.length === 0 ? (
            <div className="px-4 py-3 text-center">
              <p className="text-sm text-gray-500">No apps available</p>
              <p className="text-xs text-gray-400 mt-1">
                Central has not configured apps for this workspace.
              </p>
            </div>
          ) : (
            visibleApps.map((app) => {
              const isCurrent = app.appId === APP_ID;

              return (
                <button
                  key={app.appId}
                  onClick={async () => {
                    setIsOpen(false);
                    if (isCurrent) return;
                    // Central decides the destination, exactly as the
                    // SuiteShell path does via ui-shell 1.15.0's
                    // `config.requestAppLaunch`.
                    //
                    // This previously navigated to `launchUrl ?? webUrl` read
                    // out of the boot `apps[]` snapshot. `webUrl` is the app's
                    // DISPLAY url: it carries no authentication, so it drops
                    // the user on an app that does not know who they are — a
                    // login screen inside a suite they are already signed into
                    // — while bypassing admission, visibility, launchability
                    // and OAuth-client registration. `apps[]` is a snapshot, so
                    // entitlement may have changed since boot and it can offer
                    // a launch Central would refuse.
                    //
                    // A refusal stays a refusal. There is no best-effort launch
                    // for a first-party app: an unlaunchable answer, a missing
                    // URL, and an unreachable Central all navigate nowhere.
                    const decision = await requestCanonicalAppLaunch(app.appId);
                    if (!decision.launchable || !decision.launchUrl) {
                      console.warn(
                        '[AppSelector] Central refused the launch; not navigating.',
                        {
                          appId: app.appId,
                          reason: decision.launchable ? 'no-url' : decision.reason,
                        },
                      );
                      return;
                    }
                    window.location.assign(decision.launchUrl);
                  }}
                  disabled={isCurrent}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                    isCurrent
                      ? 'bg-blue-50 text-blue-700 font-medium cursor-default'
                      : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
                  }`}
                >
                  {app.iconUrl ? (
                    <img
                      src={app.iconUrl}
                      alt=""
                      className="w-5 h-5 rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-5 h-5 rounded bg-gray-200 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-gray-500">
                        {app.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="truncate">{app.name}</span>
                  {isCurrent ? (
                    <Check className="w-3.5 h-3.5 text-blue-600 ml-auto flex-shrink-0" />
                  ) : (
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
